import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SystemId } from '../emulator/types';
import { ZoneVisual, zoneStyle } from './VirtualPad';
import {
  MAX_ZONE_SCALE,
  MIN_ZONE_SCALE,
  PORTRAIT_CHASSIS_CLASS,
  clampPosition,
  resolveLayout,
  zonesFor,
  type DirectionalStyle,
  type PadLayout,
  type ZoneDef,
} from './padLayout';

export interface PadLayoutEditorProps {
  system: SystemId;
  analog: boolean;
  orientation: 'portrait' | 'landscape';
  padScale: number;
  padOpacity: number;
  /** 方向入力の種類。編集画面でも実際に出るものを見せる。 */
  directionalStyle?: DirectionalStyle;
  initial: PadLayout;
  onSave: (layout: PadLayout) => void;
  onCancel: () => void;
}

const EMPTY = new Set<never>();

/**
 * バーチャルパッドの配置編集。
 *
 * 指の届く位置は手の大きさと持ち方で全く違うので、
 * 既定配置をどう詰めても万人には合わない。動かせるようにするのが早い。
 *
 * 編集中は入力を一切コアへ流さない。VirtualPad とは別のコンポーネントに
 * しているのはそのためで、「編集モードのつもりが操作していた」事故を
 * 構造的に防いでいる。
 */
export function PadLayoutEditor({
  system,
  analog,
  orientation,
  padScale,
  padOpacity,
  directionalStyle = 'dpad',
  initial,
  onSave,
  onCancel,
}: PadLayoutEditorProps) {
  const zones = useMemo(
    () => zonesFor(system, { analog, directionalStyle }),
    [system, analog, directionalStyle],
  );
  const [layout, setLayout] = useState<PadLayout>(initial);
  const [selected, setSelected] = useState<string | null>(null);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ id: string; dx: number; dy: number } | null>(null);

  const current = useCallback(
    (zone: ZoneDef) => resolveLayout(zone, orientation, layout),
    [orientation, layout],
  );

  /* ---------------------------------------------------------------- */
  /* ドラッグ                                                          */
  /* ---------------------------------------------------------------- */

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const rectOf = () => root.getBoundingClientRect();

    const onDown = (e: PointerEvent) => {
      const target = (e.target as HTMLElement).closest('[data-zone]');
      const id = target?.getAttribute('data-zone');
      if (!id) return;

      e.preventDefault();
      const zone = zones.find((z) => z.id === id);
      if (!zone) return;

      const r = rectOf();
      const pos = resolveLayout(zone, orientation, layout);
      // 掴んだ位置と中心のずれを保つ（ドラッグ開始時に飛ばない）
      dragRef.current = {
        id,
        dx: pos.x - (e.clientX - r.left) / r.width,
        dy: pos.y - (e.clientY - r.top) / r.height,
      };
      setSelected(id);
      root.setPointerCapture(e.pointerId);
    };

    const onMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      e.preventDefault();

      const r = rectOf();
      const next = clampPosition(
        (e.clientX - r.left) / r.width + drag.dx,
        (e.clientY - r.top) / r.height + drag.dy,
      );

      setLayout((prev) => {
        const zone = zones.find((z) => z.id === drag.id);
        if (!zone) return prev;
        const base = resolveLayout(zone, orientation, prev);
        return { ...prev, [drag.id]: { ...base, ...next } };
      });
    };

    const onUp = () => {
      dragRef.current = null;
    };

    const opt = { passive: false } as AddEventListenerOptions;
    root.addEventListener('pointerdown', onDown, opt);
    root.addEventListener('pointermove', onMove, opt);
    root.addEventListener('pointerup', onUp);
    root.addEventListener('pointercancel', onUp);
    return () => {
      root.removeEventListener('pointerdown', onDown, opt);
      root.removeEventListener('pointermove', onMove, opt);
      root.removeEventListener('pointerup', onUp);
      root.removeEventListener('pointercancel', onUp);
    };
  }, [zones, orientation, layout]);

  /* ---------------------------------------------------------------- */

  const selectedZone = zones.find((z) => z.id === selected);

  const setScale = (value: number) => {
    if (!selectedZone) return;
    setLayout((prev) => {
      const base = resolveLayout(selectedZone, orientation, prev);
      return { ...prev, [selectedZone.id]: { ...base, scale: value } };
    });
  };

  return (
    <div className="absolute inset-0 z-[55]">
      {/* 編集対象のパッド。縦持ちは枠内座標系（プレイ時と同じ） */}
      <div
        ref={rootRef}
        className={
          orientation === 'portrait'
            ? `${PORTRAIT_CHASSIS_CLASS} touch-none select-none`
            : 'absolute inset-0 touch-none select-none'
        }
        style={{ opacity: Math.max(padOpacity, 0.75), '--pad-scale': padScale } as React.CSSProperties}
      >
        {orientation === 'portrait' && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-[2rem] border border-dashed border-white/25 bg-black/20"
          />
        )}
        {zones.map((zone) => (
          <div
            key={zone.id}
            data-zone={zone.id}
            style={zoneStyle(zone, orientation, layout)}
            className={
              selected === zone.id
                ? 'rounded-2xl outline-dashed outline-2 outline-offset-4 outline-accent'
                : 'rounded-2xl outline-dashed outline-1 outline-offset-2 outline-white/25'
            }
          >
            <ZoneVisual
              zone={zone}
              pressed={EMPTY as unknown as Set<never>}
              system={system}
              hardwareSkin={orientation === 'portrait'}
            />
          </div>
        ))}
      </div>

      {/* 操作パネル。縦は上（Start/Select を避け）、横は下（L/R を避け） */}
      <div
        className={
          orientation === 'landscape'
            ? 'pointer-events-none absolute inset-x-0 bottom-0 z-10 pb-[max(0.5rem,env(safe-area-inset-bottom))]'
            : 'pt-safe-header pointer-events-none absolute inset-x-0 top-0 z-10'
        }
      >
        {orientation === 'portrait' && (
          <p className="px-3 text-[0.65rem] leading-snug text-slate-400 drop-shadow-sm">
            {selectedZone
              ? `「${selectedZone.label ?? selectedZone.id}」— ドラッグで移動`
              : 'ボタンをドラッグして配置'}
            <span className="text-slate-600"> · 縦持ち</span>
          </p>
        )}

        {selectedZone && (
          <div
            className={`pointer-events-auto mx-3 rounded-lg border border-base-600/80 bg-base-900/95 px-2 py-1 ${
              orientation === 'landscape' ? 'mb-2' : 'mt-1'
            }`}
          >
            <label className="sr-only" htmlFor="zone-scale">
              大きさ
            </label>
            <input
              id="zone-scale"
              type="range"
              min={MIN_ZONE_SCALE}
              max={MAX_ZONE_SCALE}
              step={0.05}
              value={current(selectedZone).scale}
              onChange={(e) => setScale(Number(e.target.value))}
              className="w-full accent-accent"
              aria-valuetext={`${current(selectedZone).scale.toFixed(2)}倍`}
            />
          </div>
        )}

        <div
          className={`pointer-events-auto mx-3 grid grid-cols-3 gap-2 rounded-xl border border-base-600/80 bg-base-900/95 p-2 ${
            orientation === 'landscape' ? 'mb-2' : 'mt-2'
          }`}
        >
          <button
            type="button"
            className="btn-ghost !min-h-0 !py-2 !text-xs"
            onClick={() => {
              setLayout({});
              setSelected(null);
            }}
          >
            既定
          </button>
          <button type="button" className="btn-ghost !min-h-0 !py-2 !text-xs" onClick={onCancel}>
            取消
          </button>
          <button type="button" className="btn-primary !min-h-0 !py-2 !text-xs" onClick={() => onSave(layout)}>
            保存
          </button>
        </div>

        {orientation === 'landscape' && (
          <p className="px-3 pb-1 text-center text-[0.65rem] leading-snug text-slate-400 drop-shadow-sm">
            {selectedZone
              ? `「${selectedZone.label ?? selectedZone.id}」— ドラッグで移動`
              : 'ボタンをドラッグして配置'}
            <span className="text-slate-600"> · 横持ち</span>
          </p>
        )}
      </div>
    </div>
  );
}
